import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, Link } from 'react-router-dom';

import { SpaceReadModel } from '@client/api';
import { getBuildingId, isDefinedString, TableCell, TableRow, Text, toPriceWithIntervalString, useAreaMetricString } from '@client/toolkit';

import { PortfolioPaths } from '../../portfolio.router';
import { CompaniesPaths } from '../../../companies/companies.router';

type CommercialSpacesListItemProps = {
  space?: SpaceReadModel;
  isFetching?: boolean;
}

export function CommercialSpacesListItem({ space, isFetching }: CommercialSpacesListItemProps) {
  const { t: tType } = useTranslation('type');
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (!space || !space.id || !space.discriminator) return;

    const buildingId = getBuildingId(space.path);


    navigate(generatePath(PortfolioPaths.Space, { buildingId: buildingId, spaceId: space.id, discriminator: space.discriminator }), {
      replace: true,
    });
  }, [space, navigate]);

  const area = useAreaMetricString(space?.area);
  // COMMENTED OUT: Standard leie - uncomment to restore
  // const rent = toPriceWithIntervalString(space?.rent)

  return (
    <TableRow hover={!!space?.id} onClick={handleClick}>
      <TableCell loading={isFetching}>
        <Text className="font-semibold" isEmpty={!isDefinedString(space?.name)}>
          {space?.name}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        {space?.tenantId ? (
          <Link
            to={generatePath(CompaniesPaths.Company, { id: space.tenantId })}
            onClick={(e) => e.stopPropagation()}
            className="text-primary-400 hover:text-primary-300 hover:underline"
          >
            {space.tenantName}
          </Link>
        ) : (
          <Text isEmpty={!isDefinedString(space?.tenantName)} emptyValue="-">
            {space?.tenantName}
          </Text>
        )}
      </TableCell>

      <TableCell loading={isFetching}>
        <span style={space?.agreementStatus === 'Effective' ? { color: '#10b981' } : undefined}>
          <Text isEmpty={!isDefinedString(space?.agreementStatus)} emptyValue="-">
            {space?.agreementStatus ? tType(`AgreementStatus.${space?.agreementStatus}`) : '-'}
          </Text>
        </span>
      </TableCell>

      <TableCell loading={isFetching}>
        <span style={space?.status === 'Rented' ? { color: '#10b981' } : undefined}>
          <Text isEmpty={!isDefinedString(space?.status)} emptyValue={<span className="italic text-gray-600">{tType('VacancyStatus.undefined')}</span>}>
            {tType(`VacancyStatus.${space?.status}`)}
          </Text>
        </span>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(area)}>
          {area}
        </Text>
      </TableCell>

      {/* COMMENTED OUT: Standard leie - uncomment to restore
      <TableCell loading={isFetching} align="right">
        <Text isEmpty={!isDefinedString(rent)}>
          {rent}
        </Text>
      </TableCell>
      */}
    </TableRow>
  );
}
